/**
 * patientApi.js — Thin client for the patient search / read endpoints.
 *
 * The app already has several patient write flows (add, bulk-import, ID
 * scan) but the read/search path was never extracted into a dedicated
 * client. The Booking modal (and any future lookup surface) now uses
 * this module so we have ONE place that speaks vendor `/patients` and
 * returns a consistent envelope.
 *
 * Vendor response shapes are normalized in the backend (see
 * `_normalize_patient_list` in backend/patient_routes.py). The client
 * just trusts that envelope and exposes it unchanged.
 */
import { get, post } from './httpClient';

const BASE = '/api/patients';

/**
 * Search patients by free-text (name / DOB / phone).
 *
 * @param {Object} options
 * @param {string} options.query   — search string (min 2 chars enforced by caller)
 * @param {number} [options.limit] — max results (default 20, clamped server-side)
 * @param {number} [options.page]  — pagination page (default 1)
 * @returns {Promise<Result<{patients: Patient[], total: number, page: number, limit: number, has_more: boolean}>>}
 */
export const searchPatients = ({ query, limit = 20, page = 1 } = {}) => {
  const params = {};
  if (query && String(query).trim()) params.search = String(query).trim();
  if (limit) params.limit = limit;
  if (page) params.page = page;
  return get(BASE, params);
};

/** Fetch a single patient record by id. */
export const getPatient = (patientId) => {
  if (!patientId) return Promise.resolve({ ok: false, error: { message: 'patientId required' } });
  return get(`${BASE}/${patientId}`);
};

/**
 * Register a new patient. Thin wrapper around POST /api/patients.
 * Backend enforces dedupe + tenant scoping.
 */
export const createPatient = (body) => {
  if (!body || typeof body !== 'object') {
    return Promise.resolve({ ok: false, error: { message: 'body required' } });
  }
  return post(BASE, body);
};
